import { getAccessToken, hasClientId } from '../auth/gis';
import { DemoSource } from '../drive/mock';
import { DriveSource, loadSavedRoot, saveRoot, type DriveRoot } from '../drive/source';
import { useApp } from '../store/app';
import { useDrive } from '../store/drive';
import { ensureServiceWorker } from './swBridge';

/** Switching between the demo library and a real Drive folder. Everything
 *  above the LibrarySource interface is oblivious to which is active. */

export async function activateDemo(): Promise<void> {
  useDrive.getState().set({ status: 'off', error: null, needsSignIn: false });
  await useApp.getState().setSource(new DemoSource(), 'demo');
}

export async function activateDrive(root: DriveRoot): Promise<void> {
  const drive = useDrive.getState();
  drive.set({ status: 'connecting', error: null, root, needsSignIn: false });

  try {
    await ensureServiceWorker();
    await useApp.getState().setSource(new DriveSource(root), `drive:${root.id}`);
    saveRoot(root);
    drive.set({ status: 'connected', error: null, needsSignIn: false });
  } catch (err) {
    drive.set({
      status: 'error',
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/**
 * Picks the library to open at startup.
 *
 * Drive is used when it is fully set up — a Client ID, a chosen folder, and a
 * Google session that can still be refreshed silently. Anything missing falls
 * back to the demo library rather than an error screen, with `needsSignIn` set
 * so Settings can offer a single reconnect button.
 */
export async function bootstrapSource(): Promise<void> {
  const root = loadSavedRoot();

  if (!hasClientId() || !root) {
    await activateDemo();
    return;
  }

  useDrive.getState().set({ status: 'connecting', root });

  try {
    await getAccessToken({ interactive: false });
  } catch {
    useDrive.getState().set({ status: 'off', root, needsSignIn: true, error: null });
    await activateDemo();
    return;
  }

  await activateDrive(root);
}

export function forgetDrive(): void {
  saveRoot(null);
  useDrive.getState().set({ status: 'off', root: null, error: null, needsSignIn: false });
}
